/**
 * LearnerTopDestinations — Turn 1 of Phase 1 learner-side completion.
 *
 * Two prominent black-tile destinations sitting at the very top of the
 * learner app: Book / Calendar and Homework. The two clear places a
 * learner ever needs to start their day.
 *
 * Owner directive (Apr 29, 2026):
 *   - black, bold, simple
 *   - obvious — not buried under cards
 *   - Homework splits into Pre + Post on the homework page
 *
 * Tone:
 *   - calm, confident, adult
 *   - subtle hover lift
 *   - mobile-first (stacked); side-by-side on tablet+
 */
import { useNavigate } from "react-router-dom";
import { Calendar, BookOpen } from "lucide-react";

export default function LearnerTopDestinations() {
  const nav = useNavigate();

  return (
    <section data-testid="learner-top-destinations" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <button
        type="button"
        data-testid="top-dest-book"
        onClick={() => nav("/learn/book")}
        className="group relative overflow-hidden rounded-2xl bg-slate-900 text-white px-5 py-6 text-left transition-all active:scale-[0.98] hover:bg-slate-800 ring-1 ring-slate-900/5 dark:ring-white/10"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-none">
            <Calendar className="w-6 h-6 text-white" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/60">
              Lessons
            </p>
            <h2 className="text-xl font-extrabold mt-1 leading-tight">
              Book / Calendar
            </h2>
            <p className="text-[12px] font-medium text-white/70 mt-1.5 leading-snug">
              See available slots, book a lesson, or request a different time.
            </p>
          </div>
        </div>
      </button>

      <button
        type="button"
        data-testid="top-dest-homework"
        onClick={() => nav("/learn/homework")}
        className="group relative overflow-hidden rounded-2xl bg-slate-900 text-white px-5 py-6 text-left transition-all active:scale-[0.98] hover:bg-slate-800 ring-1 ring-slate-900/5 dark:ring-white/10"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center flex-none">
            <BookOpen className="w-6 h-6 text-white" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/60">
              Learning
            </p>
            <h2 className="text-xl font-extrabold mt-1 leading-tight">
              Homework
            </h2>
            <p className="text-[12px] font-medium text-white/70 mt-1.5 leading-snug">
              Pre Homework before each lesson · Post Homework after.
            </p>
          </div>
        </div>
      </button>
    </section>
  );
}
